import * as React from 'react';
import { useId } from 'react';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  InfoHint,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Input,
  PasswordInput
} from '@bliks-io/ui';

export interface FormFieldInputProps extends Omit<React.ComponentProps<'input'>, 'prefix'> {
  /**
   * Field label
   */
  label?: string;

  /**
   * Optional explanatory tooltip rendered as an info icon next to the label.
   */
  labelHint?: React.ReactNode;

  /**
   * Field description/helper text
   */
  description?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Mark field as required
   */
  required?: boolean;

  /**
   * Use InputGroup for enhanced functionality
   */
  useInputGroup?: boolean;

  /**
   * Prefix content (icon, text, button) - requires useInputGroup=true
   */
  prefix?: React.ReactNode;

  /**
   * Suffix content (icon, text, button) - requires useInputGroup=true
   */
  suffix?: React.ReactNode;

  /**
   * Field orientation
   */
  orientation?: 'vertical' | 'horizontal' | 'responsive';

  /**
   * Additional class for Field wrapper
   */
  fieldClassName?: string;

  /**
   * HTML autocomplete attribute.
   * Defaults to "off" to prevent browser autofill on generic fields.
   * Set explicitly to enable autofill (e.g., "email", "tel").
   */
  autoComplete?: string;
}

/**
 * Reusable form field component combining Field + Input/InputGroup
 * Can be used standalone or wrapped by RHF adapter components
 */
export const FormFieldInput = React.forwardRef<HTMLInputElement, FormFieldInputProps>(
  (
    {
      label,
      labelHint,
      description,
      error,
      required = false,
      useInputGroup = false,
      prefix,
      suffix,
      orientation = 'vertical',
      fieldClassName,
      className,
      id,
      autoComplete,
      'aria-invalid': ariaInvalid,
      ...inputProps
    },
    ref
  ) => {
    // Generate unique prefix to prevent browser autofill heuristics
    const uniqueId = useId();
    const fieldPrefix = `bliks-field${uniqueId}`;

    // Extract and transform name to prevent autofill pattern matching
    const { name: originalName, ...restInputProps } = inputProps;
    const htmlName = originalName ? `${fieldPrefix}-${originalName}` : undefined;
    const fieldId = id || htmlName || fieldPrefix;

    const hasError = !!error;
    const invalid = ariaInvalid ?? hasError;

    // Common input props with autofill prevention
    const commonInputProps = {
      ref,
      id: fieldId,
      name: htmlName,
      autoComplete: autoComplete ?? 'off',
      'aria-invalid': invalid,
      'data-field': originalName, // Original field name for debugging
      className,
      ...restInputProps
    };

    // Render content based on whether InputGroup is needed
    const renderInput = () => {
      if (useInputGroup || prefix || suffix) {
        return (
          <InputGroup>
            {prefix && <InputGroupAddon align="inline-start">{prefix}</InputGroupAddon>}
            <InputGroupInput {...commonInputProps} />
            {suffix && <InputGroupAddon align="inline-end">{suffix}</InputGroupAddon>}
          </InputGroup>
        );
      }

      if (restInputProps.type === 'password') {
        const { type: _type, autoComplete: _autoComplete, ...passwordProps } = commonInputProps;
        const passwordAutocomplete =
          autoComplete === 'new-password' || autoComplete === 'current-password'
            ? autoComplete
            : undefined;
        return <PasswordInput {...passwordProps} autocomplete={passwordAutocomplete} />;
      }

      return <Input {...commonInputProps} />;
    };

    return (
      <Field orientation={orientation} className={fieldClassName} data-invalid={hasError}>
        {label && (
          <FieldLabel htmlFor={fieldId} className="flex items-center gap-1.5">
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
            {labelHint && <InfoHint content={labelHint} />}
          </FieldLabel>
        )}
        <FieldContent>
          {description && <FieldDescription>{description}</FieldDescription>}
          {renderInput()}
          {error && <FieldError>{error}</FieldError>}
        </FieldContent>
      </Field>
    );
  }
);

FormFieldInput.displayName = 'FormFieldInput';
